import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { icon } from '../../components/ui/icons';
import { showToast } from '../../components/ui/toast';

interface Pass { id: string; dance_type_name?: string; level_name?: string; competition_name: string; competition_date: string; }
interface Grade { id: string; grade_number: number; exam_date: string; exam_location: string | null; diploma_url: string | null; }

/** Irish Dance — student portal "My Progress" widget: own passes + grades (read-only). */
@customElement('cadence-irish-dance-portal-widget')
export class IrishDancePortalWidget extends LitElement {
  @state() private passes: Pass[] = [];
  @state() private grades: Grade[] = [];
  @state() private loading = true;

  static styles = css`
    :host { display: block; }
    .muted { color: var(--color-text-secondary); font-size: 0.9rem; }
    .item { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--color-border); }
    .item:last-child { border-bottom: none; }
    .item .what { font-weight: 600; }
    .item .meta { color: var(--color-text-secondary); font-size: 0.8rem; }
    .star { color: var(--color-primary); display: inline-flex; }
    a.dl { color: var(--color-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px; font-size: 0.85rem; }
    a.dl:hover { text-decoration: underline; }
    .section + .section { margin-top: var(--space-4); }
    h4 { margin: 0 0 6px; font-size: 0.95rem; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      const [passes, grades] = await Promise.all([
        api.get<Pass[]>('/portal/modules/irish-dance/passes').catch(() => []),
        api.get<Grade[]>('/portal/modules/irish-dance/grades').catch(() => []),
      ]);
      this.passes = passes; this.grades = grades;
    } finally { this.loading = false; }
  }

  private download(g: Grade) {
    api.download(`/portal/modules/irish-dance/grades/${g.id}/diploma`, `diploma-grade-${g.grade_number}`).catch((e) => showToast((e as Error).message, 'error'));
  }

  render() {
    if (this.loading) return html`<cadence-card heading=${t('irishDance.portal.title')}><p class="muted">${t('common.states.loading')}</p></cadence-card>`;
    return html`
      <cadence-card heading=${t('irishDance.portal.title')}>
        <div class="section">
          <h4>${t('irishDance.portal.passesHeading')}</h4>
          ${this.passes.length
            ? this.passes.map((p) => html`
                <div class="item">
                  <div>
                    <div class="what"><span class="star">${icon('award', 14)}</span> ${p.dance_type_name} · ${p.level_name}</div>
                    <div class="meta">${p.competition_name} · ${p.competition_date}</div>
                  </div>
                </div>`)
            : html`<p class="muted">${t('irishDance.portal.noPasses')}</p>`}
        </div>
        <div class="section">
          <h4>${t('irishDance.portal.gradesHeading')}</h4>
          ${this.grades.length
            ? this.grades.map((g) => html`
                <div class="item">
                  <div>
                    <div class="what">${t('irishDance.grades.gradeN', { n: g.grade_number })}</div>
                    <div class="meta">${g.exam_date}${g.exam_location ? ' · ' + g.exam_location : ''}</div>
                  </div>
                  ${g.diploma_url ? html`<a class="dl" href="#" @click=${(e: Event) => { e.preventDefault(); this.download(g); }}>${icon('download', 14)} ${t('irishDance.grades.download')}</a>` : ''}
                </div>`)
            : html`<p class="muted">${t('irishDance.portal.noGrades')}</p>`}
        </div>
      </cadence-card>
    `;
  }
}
